"use client";
import { ClearCartModal } from "@/components/clearCartModal/clearCartModal";
import { RegularModal } from "@/components/modals/modals";
import { ModalSpecialInstructions } from "@/components/modalSpecialInstructions/modalSpecialInstructions";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { CartItemType, OrderType } from "@/constants/enum";
import { Modifier } from "@/dto/modifier";
import { ModifierGroup } from "@/dto/modifierGroup";
import { RetailCart } from "@/models/cart/retailCart";
import { Merchant } from "@/dto/merchant";
import { ServiceCartItem } from "@/models/cart/types";
import { lightenColor } from "@/utils/lightenColor";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { calculateModifiersTotal } from "@/utils/modifierUtils";
import pom from "@/utils/pom";
import {
  LocalStorageKey,
  SessionStorageKey,
} from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useId, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AddToCartContent } from "./addToCartContent";
import { ModifierGroupDisplay } from "./itemModalModifierSelection";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";
import { ShippingCartFullModal } from "@/components/shippingCartFullModal/shippingCartFullModal";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { FEATURE_FLAG_SHIPPING } from "@/utils/constants";
import {
  isShippingAvailable,
  wouldExceedShippingCartItemLimit,
} from "@/utils/shippingUtils";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { ScheduledTime } from "@/dto/scheduledTime";

interface ItemModalProps {
  itemData: any;
  isBusinessOpen: boolean;
  merchant: Merchant;
}

const createSelectedModifiers = (
  modifierGroups: Record<string, ModifierGroup> | undefined,
  selectedModifiers: Record<string, Record<string, number>>
): Modifier[] => {
  if (!modifierGroups || Object.keys(modifierGroups).length === 0) {
    return [];
  }

  const finalModifiers: Modifier[] = [];

  for (const [groupId, selections] of Object.entries(selectedModifiers)) {
    const group = modifierGroups[groupId];
    if (!group) continue;

    for (const [modifierId, quantity] of Object.entries(selections)) {
      const modifier = group.modifiers?.find(
        (m: Modifier) => m.id === modifierId
      );
      finalModifiers.push({
        name: modifier?.name,
        id: modifier?.id,
        price: modifier?.price,
        groupId: group.id,
        quantity,
      } as Modifier);
    }
  }

  return finalModifiers;
};

const resolveProductImage = (selectedItem: any) => {
  if (!selectedItem) return null;
  if (selectedItem.imageFilename) return selectedItem.imageFilename;
  if (!selectedItem.images || selectedItem.images.length === 0) return null;

  return selectedItem.images.reduce((prev: any, current: any) => {
    if (
      current.name.includes("xxxhdpi") ||
      (current.name.includes("xxhdpi") && !prev.name.includes("xxxhdpi")) ||
      (current.name.includes("xhdpi") &&
        !prev.name.includes("xxhdpi") &&
        !prev.name.includes("xxxhdpi"))
    ) {
      return current;
    }
    return prev;
  }, selectedItem.images[0]);
};

export const ItemModal = ({
  itemData,
  isBusinessOpen,
  merchant,
}: ItemModalProps) => {
  const t = useTranslations("ItemModal");
  const [quantity, setQuantity] = useState<any>(1);
  const [showSpecialInstructions, setShowSpecialInstructions] =
    useState<boolean>(false);
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [selectedModifiers, setSelectedModifiers] = useState<any>({});
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [isAddtocartClicked, setIsAddtocartClicked] = useState<boolean>(false);
  const [isCartFullModalOpen, setIsCartFullModalOpen] =
    useState<boolean>(false);
  const titleId = useId();
  const subtitleId = useId();
  const merchantColor = useMerchantColor();
  const shippingEnabled = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_SHIPPING
  );
  const shippingAvailable = isShippingAvailable(merchant, shippingEnabled);

  const selectedItem = itemData.items[0];
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= Number.MAX_SAFE_INTEGER) {
      setQuantity(newQuantity);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const basePrice = selectedItem.price;
      const updatedTotalPrice = basePrice * quantity;
      setTotalPrice(updatedTotalPrice);
    }
  }, [selectedItem, quantity, itemData?.modifierGroups]);

  const selectableModifierGroups = Object.values(
    itemData?.modifierGroups || {}
  ).filter(
    (group: any) =>
      selectedItem?.modifierGroupIds.includes(group.id) &&
      group.modifiers?.length > 0 &&
      group.modifiers?.some((modifier: Modifier) => modifier.available)
  );

  useEffect(() => {
    if (selectedItem) {
      const basePrice = selectedItem.price;
      const modifiersTotal = calculateModifiersTotal(
        selectableModifierGroups,
        selectedModifiers
      );

      const updatedTotalPrice = (basePrice + modifiersTotal) * quantity;
      setTotalPrice(updatedTotalPrice);
    }
  }, [selectedModifiers, selectedItem, quantity, selectableModifierGroups]);

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup | undefined =
      itemData?.modifierGroups?.[groupId];
    if (!group) return;

    setSelectedModifiers((prev: any) => {
      const newSelectedModifiers = { ...prev };
      const groupSelections: { [key: string]: number } = {
        ...(newSelectedModifiers[groupId] || {}),
      };

      if (isSingleSelection) {
        newSelectedModifiers[groupId] = { [modifierId]: 1 };
        return newSelectedModifiers;
      }

      const isSelected: boolean = groupSelections[modifierId] !== undefined;

      if (isSelected) {
        delete groupSelections[modifierId];
      } else {
        const totalGroupQuantity = Object.values(groupSelections).reduce(
          (acc: number, quantity: number) => acc + quantity,
          0
        );
        if (totalGroupQuantity < group.maxAllowed) {
          groupSelections[modifierId] = 1;
        }
      }
      newSelectedModifiers[groupId] = groupSelections;
      return newSelectedModifiers;
    });
  };

  const handleModifierQuantityChange = (
    groupId: string,
    modifierId: string,
    newQuantity: number
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup | undefined =
      itemData?.modifierGroups?.[groupId];
    if (!group) return;

    if (newQuantity === 0) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = { ...prev };
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          delete newSelectedModifiers[groupId][modifierId];
        }
        return newSelectedModifiers;
      });
      return;
    }

    const groupSelections = selectedModifiers[groupId] || {};
    const otherModifiersQuantity: number = Object.keys(groupSelections).reduce(
      (acc, mId) => {
        if (mId !== modifierId) {
          return acc + groupSelections[mId];
        }
        return acc;
      },
      0
    );

    const availableQuantity: number = Math.min(
      group.maxAllowed - otherModifiersQuantity,
      99
    );

    if (newQuantity >= 1 && newQuantity <= availableQuantity) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = JSON.parse(JSON.stringify(prev));
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          newSelectedModifiers[groupId][modifierId] = newQuantity;
        }
        return newSelectedModifiers;
      });
    }
  };

  const validateMinimumSelections = () => {
    return selectableModifierGroups.every((group: any) => {
      const groupSelections: { [key: string]: number } =
        selectedModifiers[group.id] || {};
      const totalGroupQuantity = Object.values(groupSelections).reduce(
        (acc: number, quantity: number) => acc + quantity,
        0
      );
      return (
        totalGroupQuantity >= group.minRequired &&
        totalGroupQuantity <= group.maxAllowed
      );
    });
  };

  const productImage = resolveProductImage(selectedItem);
  const disableAddButton: boolean =
    !selectedItem || selectedItem?.available === false || !isBusinessOpen;

  const router = useRouter();
  const [isClearCartOpen, setIsClearCartOpen] = useState<boolean>(false);

  const openClearCart = () => setIsClearCartOpen(true);
  const handleClearCartClose = () => setIsClearCartOpen(false);

  const handleAddToCart = () => {
    if (!validateMinimumSelections()) {
      setIsAddtocartClicked(true);
      return;
    }

    const savedOrderType = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    ).orderType;
    const shippingOrderSelected =
      shippingAvailable &&
      (savedOrderType === OrderType.SHIPPING || !savedOrderType);
    const cartLimitExceeded = wouldExceedShippingCartItemLimit({
      orderType: shippingOrderSelected
        ? OrderType.SHIPPING
        : (savedOrderType ?? OrderType.PICKUP),
      cartItems: RetailCart.getItems(),
      requestedQuantity: quantity,
      maxItems: merchant.shippingPreferences?.maxItems,
    });

    if (cartLimitExceeded) {
      setIsCartFullModalOpen(true);
      return;
    }

    const finalSelectedModifiers = createSelectedModifiers(
      itemData?.modifierGroups,
      selectedModifiers
    );

    const newItem = {
      type: CartItemType.RETAIL as const,
      id: selectedItem.id,
      name: selectedItem.name,
      price: selectedItem.price,
      quantity,
      modifiers: finalSelectedModifiers,
      specialInstructions,
      subtotal: totalPrice,
      total: totalPrice,
      isAgeRestricted: selectedItem.isAgeRestricted,
      isShippable: selectedItem.isShippable,
      cartId: uuidv4(),
    };

    const serviceCart = getFromLocalStorage<ServiceCartItem[]>(
      LocalStorageKey.SERVICE_CART,
      []
    );

    if (serviceCart && serviceCart.length >= 1) {
      openClearCart();
    } else {
      RetailCart.addItem(newItem);
      setQuantity(1);
      setSpecialInstructions("");
      setSelectedModifiers({});
      closeModal();
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    router.replace("/shop", { scroll: false });
  };

  if (isCartFullModalOpen) {
    return (
      <ShippingCartFullModal
        isOpen
        maxItems={merchant.shippingPreferences?.maxItems ?? 0}
        merchantColor={merchantColor}
        onRequestClose={() => setIsCartFullModalOpen(false)}
      />
    );
  }

  return (
    <>
      {isClearCartOpen ? (
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={handleClearCartClose}
          merchantColor={merchantColor}
          addToCart={handleAddToCart}
        />
      ) : (
        <RegularModal
          isOpen={isOpen}
          onRequestClose={closeModal}
          overlayClassName="bg-opacity-75"
          contentClassName="bg-white sm:rounded-lg shadow-lg w-full max-w-full h-[100%] sm:h-[576px] sm:w-auto mx-auto overflow-hidden"
          contentLabel={t("itemDetails")}
          focusElementId={titleId}
        >
          <div className="flex flex-col sm:flex-row-reverse w-full h-full overflow-y-auto sm:overflow-y-hidden">
            {productImage && (
              <div className="flex-shrink-0 sm:w-[450px] sm:h-[576px]">
                <Image
                  width={450}
                  height={576}
                  alt={selectedItem?.name || t("serviceNameAlt")}
                  src={productImage.source}
                  className="object-cover object-center w-full h-full sm:rounded-r-lg"
                />
              </div>
            )}

            <div
              className={`flex flex-col sm:min-h-[260px] w-full ${
                productImage ? "sm:w-[450px]" : "sm:w-[530px] pb-[64px]"
              } relative h-full sm:h-auto`}
            >
              <div className="px-6 pt-6 sticky top-0 bg-white shadow-modalStickyHeader">
                <h2
                  id={titleId}
                  tabIndex={-1}
                  aria-describedby={subtitleId}
                  data-testid={applyDataTestId(pom.itemModal.name)}
                  className="pt-4 text-4xl text-[32px] font-medium text-[#373737] whitespace-normal overflow-hidden text-ellipsis focus:outline-none"
                >
                  {selectedItem?.name}
                </h2>
              </div>
              <div className="flex flex-col px-6 max-h-[400px] sm:max-h-[420px] overflow-y-visible sm:overflow-y-auto">
                <div className="py-4">
                  <div
                    data-testid={applyDataTestId(pom.itemModal.description)}
                    className="text-gray-500 break-words whitespace-pre-wrap"
                  >
                    <p id={subtitleId}>
                      {selectedItem?.description ||
                        itemData?.description ||
                        t("noDescription")}
                    </p>
                  </div>
                </div>
                {selectableModifierGroups.length > 0 && (
                  <ModifierGroupDisplay
                    {...{
                      selectableModifierGroups,
                      selectedItem,
                      selectedModifiers,
                      handleModifierChange,
                      handleModifierQuantityChange,
                      merchantColor,
                      isAddtocartClicked,
                    }}
                  />
                )}
                <ModalSpecialInstructions
                  className="mb-8 pb-[150px] sm:pb-0"
                  {...{
                    showSpecialInstructions,
                    setShowSpecialInstructions,
                    merchantColor,
                    setSpecialInstructions,
                  }}
                />
              </div>
              <div className="fixed sm:absolute bottom-0 left-0 w-full sm:h-[64px] flex flex-col sm:flex-row">
                <div
                  style={{
                    backgroundColor: lightenColor(merchantColor, 90),
                  }}
                  className="bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium sm:w-2/5 flex flex-row rounded-bl-md h-[64px]"
                >
                  <QuantityStepper
                    handleQuantityChange={handleQuantityChange}
                    isModalQuantity={true}
                    quantity={quantity}
                    merchantColor={merchantColor}
                    min={1}
                    adaName={itemData?.name}
                  />
                </div>
                <button
                  data-testid={applyDataTestId(pom.itemModal.addToCartButton)}
                  style={{
                    backgroundColor: disableAddButton
                      ? "#C7C7C7"
                      : merchantColor,
                  }}
                  className="bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl clover-button-primary font-medium sm:w-3/5 h-[64px]"
                  onClick={handleAddToCart}
                  disabled={disableAddButton}
                >
                  <AddToCartContent
                    merchant={merchant}
                    selectedItem={selectedItem}
                    totalPrice={totalPrice}
                    isBusinessOpen={isBusinessOpen}
                  />
                </button>
              </div>
            </div>
          </div>
        </RegularModal>
      )}
    </>
  );
};
